/*
This plugin is written for the grid to store its filters to the local storage when the storage key is passed.
Provides same functionality as State Persistence plugin of the DevExtreme grid (https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/StatePersistence/React/Light/)
Our current version of grid in DevExpress does not contain this plugin, so writing our own.
*/

import { Getter, Plugin } from '@devexpress/dx-react-core';
import * as React from 'react';
import { isEmptyObject } from './filtering/helpers';
import { GridState } from './filtering/types';

export interface GridStateStoringPluginProps {
  storageKey?: string;
}

export const getStateFromLocalStorage = (key: string): GridState | undefined => {
  let filterObj;
  try {
    const filterFromStorage = window.localStorage.getItem(key);
    if (filterFromStorage) {
      filterObj = JSON.parse(filterFromStorage) as GridState;
    }
  } catch (error) {
    console.log('localstorage get error: ', error);
  }
  return filterObj;
};

export const setStateToLocalStorage = (key: string, filterObj: GridState) => {
  if (filterObj && isEmptyObject(filterObj)) {
    window.localStorage.removeItem(key);
  } else {
    const { filters, sorting, grouping } = filterObj;
    const { filters: filterFromStorage, sorting: sortingFromStorage, grouping: groupingFromStorage, ...rest } = getStateFromLocalStorage(key) || {};
    const objToSave = {
      filters: filters ?? filterFromStorage,
      sorting: sorting ?? sortingFromStorage,
      grouping: grouping ?? groupingFromStorage
    };
    Object.assign(objToSave, rest);
    try {
      window.localStorage.setItem(key, JSON.stringify(objToSave));
    } catch (error) {
      console.log('localstorage set error: ', error);
    }
  }
};

export const processGridState = (storageKey: string, filterObj: GridState, isFirstRender: any, changeAction?: any): GridState => {
  let { filters, sorting, grouping } = filterObj;

  if (storageKey) {
    if (isFirstRender.current) {
      // first render,get state from local storage, if there is storage key
      const filterFromStorage = getStateFromLocalStorage(storageKey);

      // call from filters and if filter exists in storage, we set it back to grid via changeColumnFilter
      if (filterObj?.filters && filterFromStorage?.filters) {
        if (filterFromStorage.filters !== filters) {
          filters = filterFromStorage.filters;

          // trigger the action.
          changeAction &&
            filterFromStorage.filters.forEach((filter: any) => {
              const { columnName, ...rest } = filter;
              changeAction({
                columnName,
                config: {
                  ...rest
                }
              });
            });
        }
      }

      // call from sorting and if sorting exists in storage, we set it back to grid via changeColumnSorting
      if (filterObj?.sorting && filterFromStorage?.sorting) {
        if (filterFromStorage.sorting !== sorting) {
          sorting = filterFromStorage.sorting;

          // trigger the action.
          changeAction &&
            filterFromStorage.sorting.forEach((sort: any) => {
              changeAction(sort);
            });
        }
      }

      // call from grouping and if grouping exists in storage, we set it back to grid via changeColumnGrouping
      if (filterObj?.grouping && filterFromStorage?.grouping) {
        if (filterFromStorage.grouping !== grouping) {
          grouping = filterFromStorage.grouping;

          // trigger the action.
          changeAction &&
            filterFromStorage.grouping.forEach((item: any) => {
              changeAction(item);
            });
        }
      }
      isFirstRender.current = false;
    } else {
      setStateToLocalStorage(storageKey, filterObj);
    }
  }
  return { filters, sorting, grouping };
};

const GridStateStoringPlugin = ({ storageKey }: GridStateStoringPluginProps) => {
  const isFirstRenderFilters = React.useRef(true);
  const isFirstRenderSorting = React.useRef(true);
  const isFirstRenderGrouping = React.useRef(true);

  // We adds a prefix, so that, we can clear it when needed. if someone used the same, thats bad luck!
  const prefixedKey = `AR_GRID~${storageKey}`;
  return (
    <Plugin name="gridStateStoring">
      <Getter
        name="filters"
        computed={({ filters }, { changeColumnFilter }) => {
          const { filters: updatedFilter } = processGridState(
            prefixedKey,
            {
              filters
            },
            isFirstRenderFilters,
            changeColumnFilter
          );
          return updatedFilter ? updatedFilter : [];
        }}
      />

      <Getter
        name="sorting"
        computed={({ sorting }, { changeColumnSorting }) => {
          const { sorting: updatedSort } = processGridState(
            prefixedKey,
            {
              sorting
            },
            isFirstRenderSorting,
            changeColumnSorting
          );
          return updatedSort ? updatedSort : [];
        }}
      />

      <Getter
        name="grouping"
        computed={({ grouping }, { changeColumnGrouping }) => {
          const { grouping: updatedGrouping } = processGridState(
            prefixedKey,
            {
              grouping
            },
            isFirstRenderGrouping,
            changeColumnGrouping
          );
          return updatedGrouping ? updatedGrouping : [];
        }}
      />
    </Plugin>
  );
};
export { GridStateStoringPlugin };
